import type { Messages } from "@/i18n/locales/en";
import { legalMessagesDa } from "@/i18n/locales/legalDaData";
import { pagesMessagesDa } from "@/i18n/locales/pagesDaData";

export const daMessages = {
  meta: {
    description:
      "WasOK — scan produkter, gem bedømmelser og noter, husk hvad der var værd at købe.",
    title: "WasOK — Husk hvad der var værd at købe",
  },
  header: {
    navFeatures: "Funktioner",
    navHowItWorks: "Sådan virker det",
    navPrivacy: "Privatliv",
    navUseCases: "Anvendelser",
    download: "Download",
    openMenu: "Åbn menu",
    closeMenu: "Luk menu",
    languageAria: "Sprog",
  },
  hero: {
    badge: "📱 Nu tilgængelig på Android",
    title: "Husk hvad der var værd at købe.",
    subtitle:
      "Scan produkter, gem bedømmelser og noter, og husk med det samme, om et produkt faktisk var godt.",
    googlePlay: "Download på Google Play",
    watchDemo: "Se demo",
    privacyLine: "Alle data forbliver på din enhed.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Scan for at huske",
    snackCard: "Lækkert!",
    coffeeCard: "For bitter",
    scrollHint: "Scroll for at udforske",
  },
  problems: {
    heading: "Lyder det bekendt?",
    subheading:
      "Vi har alle været der — stået foran hylderne og prøvet at huske, om vi kunne lide det sidst.",
    notedAgo: "✓ Du noterede dette for 3 måneder siden",
    items: [
      {
        question: "Hvilken chokolade kunne jeg egentlig lide?",
        note: "Perfekt sødme",
      },
      {
        question: "Hvorfor stoppede jeg med at købe denne shampoo?",
        note: "Tørrede mit hår ud",
      },
      {
        question: "Var denne kaffe god eller skuffende?",
        note: "For bitter",
      },
    ],
  },
  howItWorks: {
    heading: "Sådan virker det",
    subheading:
      "Fire enkle trin, så du aldrig glemmer dine produktmeninger igen.",
    stepPrefix: "Trin",
    steps: [
      {
        title: "Scan enhver produktstregkode",
        description:
          "Hurtig og nem scanning med kameraet. Virker med alle stregkoder.",
      },
      {
        title: "Gem bedømmelser, kommentarer og fotos",
        description:
          "Bedøm produkter, tilføj noter, vedhæft fotos, og log priser og steder.",
      },
      {
        title: "Husk din oplevelse med det samme",
        description:
          "Scan det samme produkt senere, og se din tidligere bedømmelse og noter.",
      },
      {
        title: "Byg smarte indkøbslister",
        description:
          "Opret indkøbslister ud fra produkter, du allerede stoler på og elsker.",
      },
    ],
  },
  privacy: {
    heading: "Din indkøbshistorik forbliver din.",
    body: "WasOK hjælper dig med at huske produkter, du har prøvet, mens dine bedømmelser, noter, fotos og indkøbslister gemmes lokalt på din enhed.",
    trustBanner:
      "💚 Bygget omkring din indkøbshistorik — ikke omkring at indsamle dine data.",
    bullets: [
      "Ingen konto påkrævet",
      "Ingen cloud-synkronisering påkrævet",
      "Lokal-først lagring",
      "Brugerstyrede sikkerhedskopier",
      "Valgfri stregkodesøgning efter produktinformation.",
      "Annoncefinansieret med Google AdMob",
    ],
  },
  features: {
    heading: "Alt hvad du behøver",
    subheading:
      "Kraftfulde funktioner designet til at gøre produktsporing enkel og privat.",
    items: [
      {
        title: "Stregkodescanner",
        description: "Hurtig og præcis stregkodescanning for ethvert produkt.",
      },
      {
        title: "Produktbedømmelser",
        description: "Bedøm produkter fra 1–5 stjerner for at huske kvaliteten.",
      },
      {
        title: "Produktfotos",
        description: "Vedhæft fotos for at huske emballage eller detaljer.",
      },
      {
        title: "Kategorier",
        description: "Organiser produkter i egne kategorier.",
      },
      {
        title: "Butikssporing",
        description: "Husk hvor du købte hvert produkt.",
      },
      {
        title: "Prislogning",
        description: "Spor priser for at finde de bedste tilbud.",
      },
      {
        title: "Indkøbslister",
        description: "Byg lister ud fra dine yndlingsprodukter.",
      },
      {
        title: "Offlineadgang",
        description: "Virker perfekt uden internetforbindelse.",
      },
      {
        title: "Lokal lagring",
        description: "Alle dine data gemmes sikkert på din enhed.",
      },
    ],
  },
  useCases: {
    heading: "Perfekt til hver gang",
    subheading:
      "Uanset om du handler mad, beauty eller husholdning — WasOK hjælper dig med at huske.",
    cards: [
      {
        emoji: "🍫",
        title: "Snacks & slik",
        description: "Husk hvilke slik der faktisk var gode.",
      },
      {
        emoji: "☕",
        title: "Kaffe & drikke",
        description: "Spor kaffer, der er værd at købe igen.",
      },
      {
        emoji: "💄",
        title: "Kosmetik & hudpleje",
        description: "Behold noter om hudreaktioner.",
      },
      {
        emoji: "🧹",
        title: "Husholdning",
        description: "Undgå at købe produkter, der skuffede, igen.",
      },
    ],
  },
  screenshots: {
    heading: "Smukt & intuitivt",
    subheading:
      "Et gennemtænkt interface, der gør produktsporing til en fornøjelse.",
    items: [
      { title: "Scanner" },
      { title: "Produktdetalje" },
      { title: "Bedømmelse" },
      { title: "Søgning & historik" },
      { title: "Indkøbsliste" },
    ],
  },
  cta: {
    heading: "Indkøbshukommelse til det virkelige liv.",
    body: "Hold op med at glemme, hvilke produkter der var værd at købe. Begynd at handle smartere i dag.",
    googlePlay: "Download på Google Play",
    scanQr: "Scan for at downloade",
    tagline: "Privat · Offline-først · Enkelt",
  },
  footer: {
    tagline: "Husk hvad der var værd at købe.",
    linksHeading: "Links",
    privacyPolicy: "Privatlivspolitik",
    terms: "Servicevilkår",
    contact: "Kontakt",
    linkFaq: "FAQ",
    linkLicenses: "Open source-licenser",
    linkAdvertising: "Reklamer & monetisering",
    downloadHeading: "Download",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Alle rettigheder forbeholdes. Lavet med 💚 til smartere indkøb.",
  },
  legal: legalMessagesDa,
  faq: pagesMessagesDa.faq,
  support: pagesMessagesDa.support,
  advertising: pagesMessagesDa.advertising,
} satisfies Messages;
